// TT-288: Meta Pixel from our proxy, Pfida only. The stub queues calls with consent revoked;
// Meta's script is fetched only on a marketing yes (Legal, 5 October 2026).

const PFIDA_TENANT_ID = 'pfida-5a34D';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push?: Fbq;
  loaded?: boolean;
  version?: string;
};

type MetaPixelWindow = Window & {
  fbq?: Fbq;
  _fbq?: Fbq;
  pfMetaPixelLoaded?: boolean;
  // TT-291: grant waits for Meta's script; a revoke always applies at once
  pfMetaPixelScriptReady?: boolean;
  pfMetaPixelGrantOnLoad?: boolean;
  // set until the script is injected on the first yes
  pfMetaPixelScriptUrl?: string;
};

type MetaPixelSettings = {
  tenantId: string;
  pixelId?: string;
  scriptUrl?: string;
};

function installStub(): Fbq {
  const win = window as MetaPixelWindow;
  // Meta's standard base code: queue calls until fbevents.js takes over
  const fbq = function stub(...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  win.fbq = fbq;
  if (!win._fbq) {
    win._fbq = fbq;
  }
  return fbq;
}

function injectScript(): void {
  const win = window as MetaPixelWindow;
  const scriptUrl = win.pfMetaPixelScriptUrl;
  if (!scriptUrl) {
    return;
  }
  win.pfMetaPixelScriptUrl = undefined;
  const script = document.createElement('script');
  script.async = true;
  script.src = scriptUrl;
  script.addEventListener('load', () => {
    win.pfMetaPixelScriptReady = true;
    if (win.pfMetaPixelGrantOnLoad) {
      win.pfMetaPixelGrantOnLoad = false;
      win.fbq?.('consent', 'grant');
    }
  });
  document.head.appendChild(script);
}

/**
 * Returns true when this call loaded the Pixel. Does nothing for Waafi, when a
 * build setting is missing, or when already loaded; never throws.
 */
export function loadMetaPixel({ tenantId, pixelId, scriptUrl }: MetaPixelSettings): boolean {
  try {
    const win = window as MetaPixelWindow;
    if (tenantId !== PFIDA_TENANT_ID || !pixelId || !scriptUrl || win.pfMetaPixelLoaded) {
      return false;
    }
    win.pfMetaPixelLoaded = true;

    // Tag Manager may already have created fbq; reuse it rather than load a second copy
    const existing = win.fbq;
    const fbq = existing ?? installStub();

    fbq('consent', 'revoke');
    fbq('init', pixelId);
    fbq('track', 'PageView');

    if (existing) {
      // Tag Manager owns the script; its fbq queues in order
      win.pfMetaPixelScriptReady = true;
    } else {
      win.pfMetaPixelScriptUrl = scriptUrl;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * TT-291: apply the visitor's Targeting choice. Revoke is immediate; a grant made
 * before Meta's script has loaded is held and applied on load. Never throws.
 */
export function setMetaPixelConsent(granted: boolean): void {
  try {
    const win = window as MetaPixelWindow;
    if (!win.pfMetaPixelLoaded || !win.fbq) {
      return;
    }
    if (!granted) {
      win.pfMetaPixelGrantOnLoad = false;
      win.fbq('consent', 'revoke');
      return;
    }
    if (win.pfMetaPixelScriptReady) {
      win.fbq('consent', 'grant');
    } else {
      win.pfMetaPixelGrantOnLoad = true;
      injectScript();
    }
  } catch {
    // consent must never break the app
  }
}
